import { subject } from '@casl/ability';
import { CatalogCategoryFilterMode, isCompileJob } from '@lightdash/common';
import {
    Box,
    Button,
    Group,
    Stack,
    Text,
    useMantineTheme,
    type ButtonProps,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { useNavigate, useParams } from 'react-router';
import MantineIcon from '../../../components/common/MantineIcon';
import RefreshDbtButton from '../../../components/RefreshDbtButton';
import { useProject } from '../../../hooks/useProject';
import useSearchParams from '../../../hooks/useSearchParams';
import { useTimeAgo } from '../../../hooks/useTimeAgo';
import useActiveJob from '../../../providers/ActiveJob/useActiveJob';
import useApp from '../../../providers/App/useApp';
import { useAppDispatch, useAppSelector } from '../../sqlRunner/store/hooks';
import { useIndexCatalogJob } from '../hooks/useIndexCatalogJob';
import {
    setAbility,
    setActiveMetric,
    setCategoryFilterMode,
    setCategoryFilters,
    setOrganizationUuid,
    setOwnerFilters,
    setProjectUuid,
    setSearch,
    setTableFilters,
    setTableSorting,
    setUser,
    toggleMetricExploreModal,
} from '../store/metricsCatalogSlice';
import { type MetricCatalogView } from '../types';
import { MetricChartUsageModal } from './MetricChartUsageModal';
import { MetricsTable } from './MetricsTable';

type MetricsCatalogPanelProps = {
    metricCatalogView: MetricCatalogView;
};

export const MetricsCatalogPanel: FC<MetricsCatalogPanelProps> = ({
    metricCatalogView,
}) => {
    const dispatch = useAppDispatch();
    const theme = useMantineTheme();
    const projectUuid = useAppSelector(
        (state) => state.metricsCatalog.projectUuid,
    );
    const navigate = useNavigate();
    const categoriesParam = useSearchParams('categories');
    const categoriesFilterModeParam = useSearchParams('categoriesFilterMode');
    const tablesParam = useSearchParams('tables');
    const searchParam = useSearchParams('search');
    const sortingParam = useSearchParams('sortBy');
    const sortDirectionParam = useSearchParams('sortDirection');
    const ownerUserUuidParam = useSearchParams('ownerUserUuid');

    const categories = useAppSelector(
        (state) => state.metricsCatalog.categoryFilters,
    );
    const categoryFilterMode = useAppSelector(
        (state) => state.metricsCatalog.categoryFilterMode,
    );
    const tableFilters = useAppSelector(
        (state) => state.metricsCatalog.tableFilters,
    );
    const search = useAppSelector((state) => state.metricsCatalog.search);
    const tableSorting = useAppSelector(
        (state) => state.metricsCatalog.tableSorting,
    );
    const ownerFilters = useAppSelector(
        (state) => state.metricsCatalog.ownerFilters,
    );

    const organizationUuid = useAppSelector(
        (state) => state.metricsCatalog.organizationUuid,
    );

    const [lastDbtRefreshAt, setLastDbtRefreshAt] = useState<
        Date | undefined
    >();
    const fallbackLastDbtRefreshAtRef = useRef(new Date());
    const timeAgo = useTimeAgo(
        lastDbtRefreshAt ?? fallbackLastDbtRefreshAtRef.current,
    );
    const params = useParams<{ projectUuid: string }>();
    const { data: project } = useProject(projectUuid);
    const { user } = useApp();

    // Track active compile job
    const { activeJob } = useActiveJob();
    // Track index catalog job
    const { isFetching: isIndexingCatalog } = useIndexCatalogJob(
        isCompileJob(activeJob)
            ? activeJob.jobResults?.indexCatalogJobUuid
            : undefined,
        async () => {
            setLastDbtRefreshAt(new Date());
        },
    );
    const isMetricUsageModalOpen = useAppSelector(
        (state) => state.metricsCatalog.modals.chartUsageModal.isOpen,
    );

    const onCloseMetricUsageModal = () => {
        dispatch(setActiveMetric(undefined));
    };

    const { tableName, metricName } = useParams<{
        tableName: string;
        metricName: string;
    }>();

    useEffect(() => {
        if (
            params.projectUuid &&
            (!projectUuid || projectUuid !== params.projectUuid)
        ) {
            dispatch(setProjectUuid(params.projectUuid));
        }
    }, [params.projectUuid, dispatch, projectUuid]);

    useEffect(() => {
        if (
            project &&
            (!organizationUuid || organizationUuid !== project.organizationUuid)
        ) {
            dispatch(setOrganizationUuid(project.organizationUuid));
        }
    }, [project, dispatch, organizationUuid]);

    useEffect(() => {
        const urlCategories =
            categoriesParam?.split(',').map(decodeURIComponent) || [];
        const urlCategoriesFilterMode =
            categoriesFilterModeParam === CatalogCategoryFilterMode.AND
                ? CatalogCategoryFilterMode.AND
                : CatalogCategoryFilterMode.OR;
        const urlTables = tablesParam?.split(',').map(decodeURIComponent) || [];
        const urlSearch = searchParam
            ? decodeURIComponent(searchParam)
            : undefined;
        const urlSortByParam = sortingParam
            ? decodeURIComponent(sortingParam)
            : undefined;
        const urlSortDirectionParam = sortDirectionParam
            ? decodeURIComponent(sortDirectionParam)
            : undefined;
        const urlOwnerUserUuids =
            ownerUserUuidParam?.split(',').map(decodeURIComponent) || [];

        dispatch(setCategoryFilters(urlCategories));
        dispatch(setCategoryFilterMode(urlCategoriesFilterMode));
        dispatch(setTableFilters(urlTables));
        dispatch(setOwnerFilters(urlOwnerUserUuids));
        dispatch(setSearch(urlSearch));

        if (urlSortByParam) {
            dispatch(
                setTableSorting([
                    {
                        id: urlSortByParam,
                        desc: urlSortDirectionParam === 'desc',
                    },
                ]),
            );
        }
    }, [
        categoriesParam,
        categoriesFilterModeParam,
        tablesParam,
        ownerUserUuidParam,
        dispatch,
        searchParam,
        sortingParam,
        sortDirectionParam,
    ]);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);

        if (categories.length > 0) {
            queryParams.set(
                'categories',
                categories.map(encodeURIComponent).join(','),
            );
            // Only include mode when categories selected and mode is not default (OR)
            if (categoryFilterMode === CatalogCategoryFilterMode.AND) {
                queryParams.set('categoriesFilterMode', categoryFilterMode);
            } else {
                queryParams.delete('categoriesFilterMode');
            }
        } else {
            queryParams.delete('categories');
            queryParams.delete('categoriesFilterMode');
        }

        if (tableFilters.length > 0) {
            queryParams.set(
                'tables',
                tableFilters.map(encodeURIComponent).join(','),
            );
        } else {
            queryParams.delete('tables');
        }

        if (search) {
            queryParams.set('search', encodeURIComponent(search));
        } else {
            queryParams.delete('search');
        }

        if (tableSorting.length > 0) {
            // TODO: Handle multiple sorting - this needs to be enabled and handled later in the backend
            queryParams.set('sortBy', encodeURIComponent(tableSorting[0].id));
            queryParams.set(
                'sortDirection',
                encodeURIComponent(tableSorting[0].desc ? 'desc' : 'asc'),
            );
        }

        if (ownerFilters.length > 0) {
            queryParams.set(
                'ownerUserUuid',
                ownerFilters.map(encodeURIComponent).join(','),
            );
        } else {
            queryParams.delete('ownerUserUuid');
        }

        void navigate({ search: queryParams.toString() }, { replace: true });
    }, [
        categories,
        categoryFilterMode,
        tableFilters,
        ownerFilters,
        search,
        tableSorting,
        navigate,
    ]);

    useEffect(
        function handleAbilities() {
            if (user.data) {
                const canManageTags = user.data.ability.can(
                    'manage',
                    subject('Tags', {
                        organizationUuid: user.data.organizationUuid,
                        projectUuid,
                    }),
                );

                const canRefreshCatalog =
                    user.data.ability.can('manage', 'Job') ||
                    user.data.ability.can('manage', 'CompileProject');

                const canManageExplore = user.data.ability.can(
                    'manage',
                    subject('Explore', {
                        organizationUuid: user.data.organizationUuid,
                        projectUuid,
                    }),
                );

                const canManageMetricsTree = user.data.ability.can(
                    'manage',
                    subject('MetricsTree', {
                        organizationUuid: user.data.organizationUuid,
                        projectUuid,
                    }),
                );

                const canManageSpotlight = user.data.ability.can(
                    'manage',
                    subject('SpotlightTableConfig', {
                        organizationUuid: user.data.organizationUuid,
                        projectUuid,
                    }),
                );

                dispatch(setUser({ userUuid: user.data.userUuid }));

                dispatch(
                    setAbility({
                        canManageTags,
                        canRefreshCatalog,
                        canManageExplore,
                        canManageMetricsTree,
                        canManageSpotlight,
                    }),
                );
            }
        },
        [user.data, dispatch, projectUuid],
    );

    useEffect(
        function openMetricExploreModal() {
            if (tableName && metricName) {
                dispatch(
                    toggleMetricExploreModal({
                        name: metricName,
                        tableName,
                    }),
                );
            }
        },
        [tableName, metricName, dispatch],
    );

    const headerButtonStyles: ButtonProps['style'] = {
        borderRadius: theme.radius.md,
        border: `1px solid ${theme.colors.ldGray[2]}`,
        padding: `${theme.spacing.xxs} 10px ${theme.spacing.xxs} ${theme.spacing.xs}`,
        fontSize: theme.fontSizes.sm,
        fontWeight: 500,
        color:
            theme.colorScheme === 'dark'
                ? theme.colors.ldDark[9]
                : theme.colors.ldGray[7],
    };

    return (
        <Stack w="100%" spacing="xxl">
            <Group position="apart">
                <Box>
                    <Text color="ldGray.8" weight={600} size="xl">
                        Metrics Catalog
                    </Text>
                    <Text color="ldGray.6" size="sm" weight={400}>
                        Browse all Metrics & KPIs across this project
                    </Text>
                </Box>
                <Group spacing="xs">
                    {isIndexingCatalog ? (
                        <Button
                            size="xs"
                            variant="default"
                            leftIcon={
                                <MantineIcon
                                    size="sm"
                                    color="ldGray.7"
                                    icon={IconRefresh}
                                />
                            }
                            loading={true}
                            style={headerButtonStyles}
                        >
                            Refreshing catalog
                        </Button>
                    ) : (
                        <RefreshDbtButton
                            leftIcon={
                                <MantineIcon
                                    size="sm"
                                    color="ldGray.7"
                                    icon={IconRefresh}
                                />
                            }
                            buttonStyles={headerButtonStyles}
                            defaultTextOverride={
                                lastDbtRefreshAt
                                    ? `Last refreshed ${timeAgo}`
                                    : 'Refresh catalog'
                            }
                            refreshingTextOverride="Refreshing catalog"
                        />
                    )}
                </Group>
            </Group>
            <MetricsTable metricCatalogView={metricCatalogView} />
            <MetricChartUsageModal
                opened={isMetricUsageModalOpen}
                onClose={onCloseMetricUsageModal}
            />
        </Stack>
    );
};
